import React, { useEffect, useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { usePageExit } from "@/lib/pageTransition";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  ArrowLeft,
  ArrowRight,
  Globe,
  ShoppingBag,
  PenTool,
  Monitor,
  Search,
  Zap,
  MessageSquare,
  Wrench,
  CheckCircle2,
  Circle,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const ACCENT = "#E11D2E";
const BG = "#050505";
const MUTED = "#BDBDBD";

const MOCKUP =
  "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/k9r6lxm8_ChatGPT%20Image%2027%20juin%202026%2C%2002_33_18%281%29.png";

const LOGO =
  "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/ssol55p1_Logo%20bramsgraphic%20white.png";

const audiences = [
  "Entrepreneurs",
  "Commerçants",
  "Artisans",
  "Restaurants",
  "Salles de sport",
  "Associations",
  "Indépendants",
  "Entreprises",
];

const services = [
  {
    icon: Globe,
    title: "Site vitrine",
    desc: "Présentez votre entreprise avec un site moderne, élégant et professionnel.",
  },
  {
    icon: ShoppingBag,
    title: "Boutique en ligne",
    desc: "Vendez vos produits grâce à une boutique performante avec paiement sécurisé.",
  },
  {
    icon: PenTool,
    title: "Design sur mesure",
    desc: "Une interface unique entièrement pensée pour votre identité visuelle.",
  },
  {
    icon: Monitor,
    title: "Responsive Design",
    desc: "Votre site s'adapte parfaitement aux ordinateurs, tablettes et smartphones.",
  },
  {
    icon: Search,
    title: "Optimisation SEO",
    desc: "Structure optimisée afin d'améliorer votre visibilité sur Google.",
  },
  {
    icon: Zap,
    title: "Performance",
    desc: "Chargement ultra rapide et navigation fluide.",
  },
  {
    icon: MessageSquare,
    title: "Formulaire de contact",
    desc: "Recevez facilement les demandes de vos futurs clients.",
  },
  {
    icon: Wrench,
    title: "Maintenance & accompagnement",
    desc: "Nous restons disponibles pour assurer le suivi, les mises à jour et l'évolution de votre site.",
  },
];

export default function WebDesign() {
  const rootRef = useRef(null);
  const overlayRef = useRef(null);
  const eyebrowRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const descRef = useRef(null);
  const audiencesRef = useRef(null);
  const servicesGridRef = useRef(null);
  const ctaRef = useRef(null);
  const mockupRef = useRef(null);
  const mockupWrapRef = useRef(null);
  const exit = usePageExit();

  useEffect(() => {
    document.body.classList.add("dark");
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(overlayRef.current, {
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
        onComplete: () => {
          if (overlayRef.current) overlayRef.current.style.pointerEvents = "none";
        },
      });

      const textTargets = [
        eyebrowRef.current,
        titleRef.current,
        subtitleRef.current,
        descRef.current,
        audiencesRef.current,
        servicesGridRef.current,
        ctaRef.current,
      ].filter(Boolean);

      gsap.set(textTargets, { opacity: 0, x: -80 });
      gsap.set(mockupWrapRef.current, { opacity: 0, x: 120, scale: 0.92, rotation: 2 });

      const tl = gsap.timeline({ delay: 0.15 });
      tl.to(textTargets, {
        opacity: 1,
        x: 0,
        duration: 0.8,
        ease: "power3.out",
        stagger: 0.15,
      });
      tl.to(
        mockupWrapRef.current,
        {
          opacity: 1,
          x: 0,
          scale: 1,
          rotation: 0,
          duration: 1,
          ease: "power4.out",
        },
        "-=0.9"
      );

      // Continuous float
      gsap.to(mockupRef.current, {
        y: -8,
        duration: 5,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      ScrollTrigger.batch(".wd-reveal", {
        start: "top 85%",
        once: true,
        onEnter: (els) =>
          gsap.fromTo(
            els,
            { opacity: 0, y: 30 },
            { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", stagger: 0.08 }
          ),
      });
    }, rootRef);

    return () => ctx.revert();
  }, []);

  const handleMockupEnter = () => {
    gsap.to(mockupRef.current, {
      scale: 1.03,
      rotationX: 2,
      rotationY: -3,
      duration: 0.5,
      ease: "power3.out",
      transformPerspective: 1000,
    });
  };
  const handleMockupLeave = () => {
    gsap.to(mockupRef.current, {
      scale: 1,
      rotationX: 0,
      rotationY: 0,
      duration: 0.5,
      ease: "power3.out",
    });
  };

  return (
    <div
      ref={rootRef}
      data-testid="web-design-page"
      className="relative min-h-screen text-white overflow-x-hidden"
      style={{ backgroundColor: BG, fontFamily: "'Geist Sans','Inter',system-ui,sans-serif" }}
    >
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[60]"
        style={{ backgroundColor: BG, opacity: 1 }}
        aria-hidden="true"
      />

      <div
        className="pointer-events-none fixed inset-0"
        aria-hidden="true"
        style={{
          background: `radial-gradient(60% 40% at 50% 0%, ${ACCENT}10 0%, transparent 60%),
                       radial-gradient(50% 35% at 100% 100%, ${ACCENT}0a 0%, transparent 60%)`,
          zIndex: 0,
        }}
      />

      <header className="relative z-20 max-w-7xl mx-auto px-6 lg:px-10 pt-7 md:pt-10 flex items-center justify-between">
        <Link
          to="/"
          onClick={(e) => { e.preventDefault(); exit("realisations"); }}
          data-testid="wd-back-home"
          className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] opacity-75 hover:opacity-100 transition-all"
        >
          <span className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center transition-all group-hover:-translate-x-1 group-hover:border-white/50">
            <ArrowLeft size={14} />
          </span>
          Retour
        </Link>
        <Link to="/" className="flex items-center" data-testid="wd-logo">
          <img src={LOGO} alt="Brams Graphic Design" className="h-12 md:h-14 object-contain" />
        </Link>
        <div className="hidden md:flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] opacity-60">
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: ACCENT }} />
          Service · 03
        </div>
      </header>

      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* LEFT */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div
              ref={eyebrowRef}
              className="flex items-center gap-3 text-[10px] uppercase tracking-[0.4em]"
              style={{ color: MUTED }}
            >
              <span className="h-px w-8" style={{ background: ACCENT }} />
              Web Design
            </div>

            <h1
              ref={titleRef}
              data-testid="wd-title"
              className="mt-6 font-display font-bold tracking-tighter leading-[1.02] text-5xl sm:text-6xl lg:text-7xl"
              style={{ letterSpacing: "-0.04em" }}
            >
              Web <span style={{ color: ACCENT }}>Design</span>
            </h1>

            <p
              ref={subtitleRef}
              data-testid="wd-subtitle"
              className="mt-6 text-lg md:text-xl font-light"
              style={{ color: MUTED }}
            >
              Des sites internet modernes, rapides et conçus pour convertir vos visiteurs en clients.
            </p>

            <div ref={descRef} data-testid="wd-description" className="mt-10 space-y-5 max-w-2xl">
              <p className="text-base md:text-[17px] leading-relaxed font-light text-white/85">
                Votre site internet est bien plus qu&apos;une simple vitrine : il représente votre entreprise,
                valorise votre image et constitue souvent le premier contact avec vos futurs clients.
              </p>
              <p className="text-base md:text-[17px] leading-relaxed font-light text-white/80">
                Chez <strong className="font-medium">Brams Graphic</strong>, nous concevons des sites web
                sur mesure alliant design, performance et expérience utilisateur. Chaque projet est pensé
                pour offrir une navigation fluide, une esthétique moderne et une compatibilité parfaite
                sur ordinateur, tablette et smartphone.
              </p>
              <p className="text-base md:text-[17px] leading-relaxed font-light text-white/80">
                Nous développons des sites rapides, sécurisés et optimisés afin d&apos;offrir à vos
                visiteurs une expérience professionnelle tout en mettant en valeur votre activité.
              </p>
              <p className="text-base md:text-[17px] leading-relaxed font-light text-white/80">
                Que vous soyez entrepreneur, commerçant, artisan, restaurant, salle de sport ou
                entreprise, nous créons une solution adaptée à vos besoins et à votre identité visuelle.
              </p>

              <p className="text-sm uppercase tracking-[0.3em] pt-2" style={{ color: MUTED }}>
                Pour qui ?
              </p>

              <ul
                ref={audiencesRef}
                data-testid="wd-audiences"
                className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 pt-2"
              >
                {audiences.map((s) => (
                  <li
                    key={s}
                    className="flex items-center gap-3 text-[15px] font-light text-white/85"
                  >
                    <span
                      className="inline-flex w-6 h-6 items-center justify-center rounded-full"
                      style={{
                        background: "rgba(225,29,46,0.08)",
                        border: `1px solid ${ACCENT}33`,
                      }}
                    >
                      <CheckCircle2 size={12} style={{ color: ACCENT }} />
                    </span>
                    {s}
                  </li>
                ))}
              </ul>
            </div>

            {/* Services Grid */}
            <div className="mt-16">
              <div className="flex items-end justify-between gap-6 mb-8">
                <h2 className="font-display font-semibold text-2xl md:text-3xl tracking-tight">
                  Nos prestations
                </h2>
                <span
                  className="hidden md:inline-block text-[10px] uppercase tracking-[0.3em]"
                  style={{ color: MUTED }}
                >
                  08 livrables
                </span>
              </div>

              <div
                ref={servicesGridRef}
                data-testid="wd-services-grid"
                className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4"
              >
                {services.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div
                      key={s.title}
                      data-testid={`wd-service-${i}`}
                      className="wd-reveal group relative p-6 transition-all duration-500 ease-out"
                      style={{
                        background: "#0B0B0B",
                        border: "1px solid rgba(255,255,255,0.06)",
                        borderRadius: 18,
                      }}
                    >
                      <span
                        className="absolute left-0 top-0 h-[2px] w-0 group-hover:w-full transition-all duration-700"
                        style={{ background: ACCENT }}
                      />
                      <div className="flex items-start gap-4">
                        <div
                          className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-500 group-hover:scale-110"
                          style={{
                            background: "rgba(255,255,255,0.03)",
                            border: "1px solid rgba(255,255,255,0.08)",
                          }}
                        >
                          <Icon size={18} strokeWidth={1.5} style={{ color: ACCENT }} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-display font-semibold tracking-tight text-base md:text-lg">
                            {s.title}
                          </h3>
                          <p className="mt-2 text-sm leading-relaxed font-light" style={{ color: MUTED }}>
                            {s.desc}
                          </p>
                        </div>
                        <Circle
                          size={10}
                          strokeWidth={1.2}
                          className="opacity-30 group-hover:opacity-100 transition-opacity"
                          style={{ color: ACCENT }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CTA */}
            <div ref={ctaRef} className="mt-14">
              <Link
                to="/#contact"
                onClick={(e) => { e.preventDefault(); exit("contact"); }}
                data-testid="wd-cta-quote"
                className="group relative inline-flex items-center gap-3 rounded-full pl-8 pr-3 py-3.5 font-medium text-sm tracking-wide transition-all duration-300 ease-out hover:scale-[1.04] active:scale-95"
                style={{
                  background: ACCENT,
                  color: "#fff",
                  boxShadow: `0 10px 40px -10px ${ACCENT}99`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 18px 60px -10px ${ACCENT}cc, 0 0 0 6px rgba(225,29,46,0.12)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = `0 10px 40px -10px ${ACCENT}99`;
                }}
              >
                <span className="uppercase tracking-[0.25em]">Créer mon site</span>
                <span
                  className="flex items-center justify-center w-10 h-10 rounded-full transition-transform duration-500 group-hover:translate-x-1"
                  style={{ background: "rgba(0,0,0,0.25)" }}
                >
                  <ArrowRight size={16} />
                </span>
              </Link>
              <p className="mt-4 text-xs uppercase tracking-[0.3em]" style={{ color: MUTED }}>
                Devis gratuit · Réponse sous 48 h
              </p>
            </div>
          </div>

          {/* RIGHT — Mockup */}
          <div className="lg:col-span-5 order-1 lg:order-2 lg:sticky lg:top-10">
            <div ref={mockupWrapRef} className="relative" style={{ perspective: 1200 }}>
              <div
                className="absolute -inset-10 rounded-[40px] pointer-events-none"
                style={{
                  background: `radial-gradient(closest-side, ${ACCENT}26, transparent 70%)`,
                  filter: "blur(40px)",
                }}
                aria-hidden="true"
              />

              <div
                ref={mockupRef}
                onMouseEnter={handleMockupEnter}
                onMouseLeave={handleMockupLeave}
                className="relative overflow-hidden"
                data-testid="wd-mockup"
                style={{
                  borderRadius: 24,
                  background: "#0A0A0A",
                  border: "1px solid rgba(255,255,255,0.06)",
                  boxShadow:
                    "0 30px 80px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.04)",
                  transformStyle: "preserve-3d",
                  willChange: "transform",
                }}
              >
                <img
                  src={MOCKUP}
                  alt="Web Design — site BSG affiché sur MacBook, iPad Pro et iPhone"
                  className="block w-full h-auto select-none"
                  draggable={false}
                  loading="eager"
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 25%)",
                  }}
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, transparent 70%, rgba(0,0,0,0.45) 100%)",
                  }}
                />
              </div>

              <div
                aria-hidden="true"
                className="mt-3 mx-8 h-10 opacity-50"
                style={{
                  background:
                    "radial-gradient(ellipse at center, rgba(225,29,46,0.18) 0%, transparent 70%)",
                  filter: "blur(10px)",
                }}
              />

              {/* Tech stack badges */}
              <div className="wd-reveal mt-8 grid grid-cols-3 gap-3">
                {[
                  { k: "Responsive", v: "100%" },
                  { k: "Performance", v: "Optimisée" },
                  { k: "SEO", v: "Inclus" },
                ].map((b) => (
                  <div
                    key={b.k}
                    className="p-4 text-center"
                    style={{
                      background: "#0B0B0B",
                      border: "1px solid rgba(255,255,255,0.06)",
                      borderRadius: 18,
                    }}
                  >
                    <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: MUTED }}>
                      {b.k}
                    </div>
                    <div className="mt-1 font-display font-semibold text-sm tracking-tight">
                      {b.v}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA strip */}
      <section
        className="wd-reveal relative z-10 border-t border-white/[0.06] py-16 md:py-20"
        style={{ background: "#070707" }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <span className="text-[10px] uppercase tracking-[0.4em]" style={{ color: MUTED }}>
              Prêt à passer en ligne ?
            </span>
            <h3 className="mt-3 font-display font-semibold text-3xl md:text-4xl tracking-tighter">
              Lançons votre <span style={{ color: ACCENT }}>présence web</span>.
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/#contact"
              onClick={(e) => { e.preventDefault(); exit("contact"); }}
              data-testid="wd-cta-bottom-contact"
              className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-medium text-sm transition-all duration-300 hover:scale-[1.03] active:scale-95"
              style={{ background: "#fff", color: "#000" }}
            >
              Me contacter
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/"
              onClick={(e) => { e.preventDefault(); exit("realisations"); }}
              data-testid="wd-cta-bottom-home"
              className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-medium text-sm transition-all duration-300 hover:scale-[1.03] active:scale-95 border border-white/20 hover:bg-white/5"
            >
              Voir d&apos;autres services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
